<template>
  <div>
    <va-modal
      id="create-or-update-appointment-modal"
      ref="createOrUpdateAppointmentModal"
      :value="isOpen"
      :okText="$t('modals.buttons.save')"
      :cancelText="$t('modals.buttons.back')"
      @ok="submit"
      :okDisabled="!isValidSchedule || !selectedFacility || !isValidTubeNr"
      @cancel="onCancel"
      size="large"
      :noOutsideDismiss="true"
    >
      <div slot="header" class="modal-title">
        <span>{{
            isUpdate ? $t('homelab.labels.update_appointment', {name: appointment.name}) : $t('homelab.labels.new_appointment')
          }}</span>
      </div>

      <div class="modal-header-actions d-flex justify-content-end" v-if="isUpdate && deletable">
        <va-popover
          placement="left"
          :message="$t('homelab.buttons.delete_appointment')">
          <va-button
            large
            class="va-button-rounded"
            icon="fa fa-trash"
            color="gray"
            @click="prepareForDestroy">
          </va-button>
        </va-popover>
      </div>

      <form class="evollu-form">
        <div>
          <h5 class="mb-3">{{ $t('homelab.labels.client_info') }}</h5>

          <div class="form-row">

            <div class="col-12 col-md-6 mb-3">
              <va-input
                id="appointment-name"
                :label="$t('homelab.labels.name')"
                v-model="form.data.name"
                :placeholder="$t('homelab.labels.name')"
                @input="form.errors.clear('name')"
                :error="form.errors.has('name')"
                :error-messages="[$t(form.errors.get('name'))]"
              />
            </div>

            <div class="col-12 col-md-6 mb-3">
              <va-input
                id="appointment-email"
                :label="$t('homelab.labels.email')"
                v-model="form.data.email"
                :placeholder="$t('homelab.labels.email')"
                @input="form.errors.clear('email')"
                :error="form.errors.has('email')"
                :error-messages="[$t(form.errors.get('email'))]"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="col-12 col-md-6 mb-3">
              <va-input
                id="appointment-health-number"
                :label="$t('homelab.labels.health_number')"
                v-model="form.data.health_number"
                :placeholder="$t('homelab.labels.health_number')"
                @input="form.errors.clear('health_number')"
                :error="form.errors.has('health_number')"
                :error-messages="[$t(form.errors.get('health_number'))]"
              />
            </div>

            <div class="col-12 col-md-6 mb-3">
              <va-input
                id="appointment-tax-number"
                :label="$t('homelab.labels.tax_number')"
                v-model="form.data.tax_number"
                :placeholder="$t('homelab.labels.tax_number')"
                @input="form.errors.clear('tax_number')"
                :error="form.errors.has('tax_number')"
                :error-messages="[$t(form.errors.get('tax_number'))]"
              />
            </div>

          </div>

          <div class="form-row">
            <div class="col-12 col-md-6 mb-3">
              <date-picker
                id="appointment-birthdate"
                :label="$t('homelab.labels.birthdate')"
                v-model="form.data.birthdate"
                :config="{maxDate: 'today', dateFormat: 'Y-m-d', enableTime: false,}"
                @input="form.errors.clear('birthdate')"
                :error="form.errors.has('birthdate')"
                :error-messages="[$t(form.errors.get('birthdate'))]"
              />
            </div>

            <div class="col-12 col-md-6 mb-3">
              <label class="form-label" :class="{'text-danger': form.errors.has('gender')}">
                {{ $t('homelab.labels.gender') }}:
              </label>
              <multiselect
                id="appointment-gender"
                v-model="selectedGender"
                :options="genders"
                track-by="value"
                label="name"
                placeholder="---"
                :allow-empty="false"
                :multiple="false"
                @input="form.errors.clear('gender')"
              >
                <template slot="option" slot-scope="{option}">
                  {{ $t(option.name) }}
                </template>
                <template slot="singleLabel" slot-scope="{option}">
                  {{ $t(option.name) }}
                </template>
                <template slot="clear">
                  <span class="multiselect__clear" v-if="selectedGender"
                        @mousedown.prevent.stop="selectedGender = null"></span>
                </template>
              </multiselect>
              <div class="text-danger" v-if="form.errors.has('gender')">
                <small>{{ $t(form.errors.get('gender')) }}</small>
              </div>
            </div>

          </div>

          <div class="form-row">

            <div class="col-12 col-md-6 mb-3">
              <va-input
                id="appointment-phone"
                :label="$t('homelab.labels.phone')"
                v-model="form.data.phone_number"
                :placeholder="$t('homelab.labels.phone')"
                @input="form.errors.clear('phone_number')"
                :error="form.errors.has('phone_number')"
                :error-messages="[$t(form.errors.get('phone_number'))]"
              />
            </div>

            <div class="col-12 col-md-6 mb-3">
              <va-input
                id="appointment-client-number"
                :label="$t('homelab.labels.client_number')"
                v-model="form.data.client_number"
                :placeholder="$t('homelab.labels.client_number')"
                @input="form.errors.clear('client_number')"
                :error="form.errors.has('client_number')"
                :error-messages="[$t(form.errors.get('client_number'))]"
              />
            </div>

          </div>
        </div>

        <hr>

        <div>
          <h5 class="mb-3">{{ $t('homelab.labels.appointment_info') }}</h5>

          <div class="form-row">
            <div class="col-12 col-md-6 mb-3">
              <label class="form-label" :class="{'text-danger': form.errors.has('entity_id')}">
                {{ $t('homelab.labels.entity') }}:
              </label>
              <multiselect
                id="appointment-entity"
                v-model="selectedEntity"
                :options="writableEntities"
                track-by="id"
                label="name"
                placeholder="---"
                :allow-empty="false"
                :multiple="false"
                @input="form.errors.clear('entity_id')"
              >
                <template slot="clear">
                  <span class="multiselect__clear" v-if="selectedEntity"
                        @mousedown.prevent.stop="selectedEntity = null"></span>
                </template>
              </multiselect>
              <div class="text-danger" v-if="form.errors.has('entity_id')">
                <small>{{ $t(form.errors.get('entity_id')) }}</small>
              </div>
            </div>

            <div class="col-12 col-md-6 mb-3">
              <label class="form-label" :class="{'text-danger': form.errors.has('facility_id')}">
                {{ $t('homelab.labels.facility') }}:
              </label>
              <multiselect
                id="appointment-facility"
                v-model="selectedFacility"
                :options="selectedEntityFacilities"
                track-by="id"
                label="name"
                placeholder="---"
                :allow-empty="false"
                :multiple="false"
                :disabled="selectedEntityFacilities.length === 0"
                @input="form.errors.clear('facility_id')"
              >
                <template slot="clear">
                  <span class="multiselect__clear" v-if="selectedFacility"
                        @mousedown.prevent.stop="selectedFacility = null"></span>
                </template>
              </multiselect>
              <div class="text-danger" v-if="form.errors.has('facility_id')">
                <small>{{ $t(form.errors.get('facility_id')) }}</small>
              </div>
            </div>

          </div>

          <div class="form-row">
            <div class="col-12 col-md-6 mb-3" v-if="selectedFacility">
              <date-picker
                id="date-picker-selected-time"
                v-model="selectedTime"
                :label="$t('homelab.labels.selected_time')"
                :config="selectedTimeDatePickerConfig"
                @input="handleSelectedTimeInput"
                :error="form.errors.has('selected_time')"
                :error-messages="[$t(form.errors.get('selected_time'))]"
              />
              <b class="pl-2 "
                 v-if="selectedTime !== null"
                 v-bind:class="{'validText': isValidSchedule, 'invalidText': !isValidSchedule}">
                • {{ isValidSchedule ? $t('homelab.labels.valid_schedule') : $t('homelab.labels.invalid_schedule') }}
              </b>
            </div>
          </div>
          <div class="form-row">

            <h6 class="mb-3 ml-2">{{ $t('homelab.labels.prescription_info') }}</h6>

            <div class="appointment-prescription-info col-12" v-for="(p, index) in form.data.prescriptions" :key="p">
              <div class="form-row ml-1 mr-1 form-row-outline">
                <div class="col-12 col-md-6 mb-3">
                  <va-input
                    id="appointment-code-nr"
                    :label="$t('homelab.labels.code_nr')"
                    v-model="form.data.prescriptions[index].code_nr"
                    :maxlength="19"
                    :placeholder="$t('homelab.labels.code_nr')"
                    @input="form.errors.clear(`prescriptions.${index}.code_nr`)"
                    :error="form.errors.has(`prescriptions.${index}.code_nr`)"
                    :error-messages="[$t(form.errors.get(`prescriptions.${index}.code_nr`))]"
                  />
                </div>

                <div class="col-12 col-md-6 mb-3">
                  <va-input
                    id="appointment-access-code"
                    :label="$t('homelab.labels.access_code')"
                    v-model="form.data.prescriptions[index].access_code"
                    :maxlength="6"
                    :placeholder="$t('homelab.labels.access_code')"
                    @input="form.errors.clear(`prescriptions.${index}.access_code`)"
                    :error="form.errors.has(`prescriptions.${index}.access_code`)"
                    :error-messages="[$t(form.errors.get(`prescriptions.${index}.access_code`))]"
                  />
                </div>

                <div class="col-12 col-md-6 mb-3">
                  <va-input
                    id="appointment-prescription-code"
                    :label="$t('homelab.labels.prescription_code')"
                    v-model="form.data.prescriptions[index].prescription_code"
                    :maxlength="6"
                    :placeholder="$t('homelab.labels.prescription_code')"
                    @input="form.errors.clear(`prescriptions.${index}.prescription_code`)"
                    :error="form.errors.has(`prescriptions.${index}.prescription_code`)"
                    :error-messages="[$t(form.errors.get(`prescriptions.${index}.prescription_code`))]"
                  />
                </div>

                <div class="col-12 col-md-6 mb-3">
                  <va-input
                    id="appointment-tube_nr"
                    :label="$t('homelab.labels.tube_nr')"
                    v-model="form.data.prescriptions[index].tube_nr"
                    type="text"
                    :maxlength="7"
                    :placeholder="$t('homelab.labels.tube_nr')"
                    @input="form.errors.clear(`prescriptions.${index}.tube_nr`)"
                    :error="form.errors.has(`prescriptions.${index}.tube_nr`)"
                    :error-messages="[$t(form.errors.get(`prescriptions.${index}.tube_nr`))]"
                  />
                </div>
              </div>
            </div>
            <div class="col-12 w-100 d-flex justify-content-end">
              <div class="btn btn__round btn_add" v-if="newPrescriptions<5">
                <va-button
                  @click.prevent="addPrescription"
                  :disabled="isDisabled "
                >
                  <i class="fa fa-plus"></i>
                </va-button>
              </div>
              <div v-if="newPrescriptions === 5">
                <i class="fa fa-warning" style="color: #ffcc00"/>
                &nbsp;
                <label style="color: #ffcc00">
                  {{$t('homelab.errors.validation.prescriptions_filled')}}
                </label>

              </div>

            </div>

            <div class="col-12 col-md-6 mb-3">
              <label class="form-label" :class="{'text-danger': form.errors.has('insurer_id')}">
                {{ $t('homelab.labels.insurers') }}:
              </label>
              <multiselect
                id="appointment-insurer"
                v-model="selectedInsurer"
                :options="insurers"
                track-by="id"
                label="name"
                placeholder="---"
                :allow-empty="false"
                :multiple="false"
                :disabled="selectedPackages.length > 0"
                @input="form.errors.clear('insurer_id'); form.errors.clear('packages')"
              >
                <template slot="clear">
                  <span class="multiselect__clear" v-if="selectedInsurer"
                        @mousedown.prevent.stop="selectedInsurer = null"></span>
                </template>
              </multiselect>
              <div class="text-danger" v-if="form.errors.has('insurer_id')">
                <small>{{ $t(form.errors.get('insurer_id')) }}</small>
              </div>
            </div>

            <div class="col-12 col-md-6 mb-3">
              <label class="form-label" :class="{'text-danger': form.errors.has('packages')}">
                {{ $t('homelab.labels.packages') }}:
              </label>
              <multiselect
                id="appointment-packages"
                v-model="selectedPackages"
                :options="packages"
                track-by="id"
                label="name"
                placeholder="---"
                :allow-empty="true"
                :multiple="true"
                :disabled="selectedInsurer != null"
                :close-on-select="false"
                @input="form.errors.clear('packages'); form.errors.clear('insurer_id')"
              >
                <template slot="clear">
                  <span class="multiselect__clear" v-if="selectedPackages.length > 0"
                        @mousedown.prevent.stop="selectedPackages = []"></span>
                </template>
              </multiselect>
              <div class="text-danger" v-if="form.errors.has('packages')">
                <small>{{ $t(form.errors.get('packages')) }}</small>
              </div>
            </div>

            <div class="col-12 col-md-6 mb-3">
              <va-input
                id="appointment-total-price"
                :label="$t('homelab.labels.total_price')"
                v-model.number="form.data.total_price"
                :placeholder="$t('homelab.labels.total_price')"
                type="number"
                @input="form.errors.clear('total_price')"
                :error="form.errors.has('total_price')"
                :error-messages="[$t(form.errors.get('total_price'))]"
              />
            </div>

          </div>

          <div class="form-row align-items-end">

            <!--<div class="col-12 col-md-6" v-if="isValidSchedule !== null">

            </div>-->
            <div class="col-12 col-md-6" style="margin-bottom: 65px !important;">
              <va-checkbox
                v-model="form.data.has_medical_prescription"
                :label="$t('homelab.labels.has_medical_prescription')"
              />
            </div>
          </div>
        </div>

        <hr>

        <div>
          <h5 class="mb-4">{{ $t('homelab.labels.collection_address') }}</h5>
          <div class="row">
            <div class="col-12 d-flex justify-content-start">
              <span class="mr-3 is-bold">
                {{ $t('homelab.labels.collect_at_home') }}
              </span>
              <Slider v-model="form.data.collect_at_home"/>
            </div>
          </div>
          <expand-transition>
            <div class="row" v-if="form.data.collect_at_home" key="address-form">

              <div class="col-12 col-md-6 pr-md-2">

                <div class="mb-3">
                  <va-input
                    id="appointment-address-line1"
                    :label="$t('address.line1')"
                    :placeholder="$t('address.line1')"
                    v-model="form.data.address.line1"
                    @input="form.errors.clear('address.line1')"
                    :error="form.errors.has('address.line1')"
                    :error-messages="[$t(form.errors.get('address.line1'))]"
                  />
                </div>

                <div class="mb-3">
                  <va-input
                    id="appointment-address-line2"
                    :label="$t('address.line2')"
                    :placeholder="$t('address.line2')"
                    v-model="form.data.address.line2"
                    @input="form.errors.clear('address.line2')"
                    :error="form.errors.has('address.line2')"
                    :error-messages="[$t(form.errors.get('address.line2'))]"
                  />
                </div>

                <div class="mb-3">
                  <va-input
                    id="appointment-address-door-number"
                    :label="$t('address.door_number')"
                    :placeholder="$t('address.door_number')"
                    v-model="form.data.address.door_number"
                    @input="form.errors.clear('address.door_number')"
                    :error="form.errors.has('address.door_number')"
                    :error-messages="[$t(form.errors.get('address.door_number'))]"
                  />
                </div>

                <div class="mb-3">
                  <va-input
                    id="appointment-address-city"
                    :label="$t('address.city')"
                    :placeholder="$t('address.city')"
                    v-model="form.data.address.city"
                    @input="form.errors.clear('address.city')"
                    :error="form.errors.has('address.city')"
                    :error-messages="[$t(form.errors.get('address.city'))]"
                  />
                </div>

                <div class="mb-4">
                  <va-input
                    id="appointment-address-postal-code"
                    :label="$t('address.postal_code')"
                    :placeholder="$t('address.postal_code')"
                    v-model="form.data.address.postal_code"
                    @input="form.errors.clear('address.postal_code')"
                    :error="form.errors.has('address.postal_code')"
                    :error-messages="[$t(form.errors.get('address.postal_code'))]"
                  />
                </div>

                <div class="mb-3 text-danger text-center" v-if="map.message">
                  <small>
                    {{ map.message }}
                  </small>
                </div>

              </div>

              <div class="col-12 col-md-6 pl-md-3">
                <google-map
                  ref="googleMap"
                  height="50vh"
                  :locations="map.locations"/>
              </div>

            </div>
          </expand-transition>
        </div>

      </form>

    </va-modal>

    <confirmation-modal v-if="isDestroyingAppointment"
                        :body="$t('homelab.messages.confirm_delete', {name: appointment.name})"
                        @modal-canceled="cancelDestroyAppointment"
                        @modal-confirmed="confirmDestroyAppointment"/>

  </div>
</template>
<script>
import {mapState} from 'vuex'
import Multiselect from 'vue-multiselect'
import DatePicker from '@/components/common/DatePicker'
import GoogleMap from '@/components/maps/google-maps/GoogleMap'
import AddressConverter from '@/utils/AddressConverter'
import {debounce} from 'lodash'
import {AppointmentService} from '@/api/HomelabService'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import Errors from '@/utils/CustomHomelabErrors'
import Slider from '@/components/common/Slider'
import ExpandTransition from '@/components/common/transitions/ExpandTransition'
import {FacilityScheduleService} from '@/api/AuthService'
import moment from 'moment-timezone'

export default {
  props: {
    appointment: {
      type: Object,
      required: false
    },
    deletable: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  components: {
    Multiselect,
    Slider,
    DatePicker,
    GoogleMap,
    ConfirmationModal,
    ExpandTransition
  },
  computed: {
    ...mapState('homelab', [
      'insurers',
      'packages'
    ]),
    isDisabled: function () {
      if (this.newPrescriptions === 5) {
        return true
      }
      return this.form.data.prescriptions[this.newPrescriptions - 1].code_nr === null && this.form.data.prescriptions[this.newPrescriptions - 1].access_code === null && this.form.data.prescriptions[this.newPrescriptions - 1].prescription_code === null && this.form.data.prescriptions[this.newPrescriptions - 1].tube_nr === null;

    },
    authServiceToken: function () {
      return this.$store.state.auth.authSrvToken
    },
    isUpdate: function () {
      return this.appointment !== null && this.appointment !== undefined
    },
    homelabService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.homelab_server)
    },
    writableEntities: function () {
      let appointmentPermissions = this.homelabService.user_permissions.filter(permission => permission.access_type >= 2 && permission.info_type === 100)
      if (appointmentPermissions.length > 0) {
        if (appointmentPermissions.some(permission => permission.entity_id === null)) {
          return [...this.homelabService.entities]
        } else {
          return appointmentPermissions.map(permission => this.homelabService.entities.find(entity => permission.entity_id === entity.id)).filter(v => v)
        }
      } else {
        return []
      }
    },
    selectedTimeDatePickerConfig: function () {
      let hour = this.moment().hour() + 1
      let day = 'today'

      /* if (hour > 20) {
        hour = '09'
        day = 'tomorrow'
      } */

      return {
        enableTime: true,
        dateFormat: 'Y-m-d H:i',
        time_24hr: true,
        minDate: day,
        defaultHour: `${hour}`
      }
    },
    selectedEntityFacilities: function () {
      return this.selectedEntity ? this.selectedEntity.facilities : []
    },
    location: function () {
      if (this.form.data.address.lat && this.form.data.address.lng) {
        return [
          {
            position: {
              lat: this.form.data.address.lat,
              lng: this.form.data.address.lng
            }
          }
        ]
      } else {
        return []
      }
    }
  },
  data: function () {
    return {
      newPrescriptions: 1,
      isOpen: false,
      isLoading: false,
      isDestroyingAppointment: false,
      isValidTubeNr: {
        0: true,
        1: true,
        2: true,
        3: true,
        4: true
      },
      genders: [
        {name: 'gender.female', value: 'female'},
        {name: 'gender.male', value: 'male'},
        {name: 'gender.not_disclosed', value: 'not_disclosed'}
      ],
      selectedEntity: null,
      selectedFacility: null,
      selectedInsurer: null,
      selectedPackages: [],
      selectedGender: null,
      selectedTime: null,
      originalSelectedTime: null,
      facilitySchedule: [],
      selectedTimeSettedByUser: false,
      facilityChanged: false,
      isValidSchedule: null,
      map: {
        addressConverter: new AddressConverter(),
        locations: [],
        message: null
      },
      form: {
        data: {
          entity_id: null,
          facility_id: null,
          insurer_id: null,
          packages: [],
          name: null,
          email: null,
          gender: null,
          birthdate: null,
          collect_at_home: false,
          address: {
            line1: '',
            line2: null,
            city: null,
            postal_code: '',
            door_number: null,
            lat: null,
            lng: null
          },
          selected_time: null,
          phone_number: null,
          client_number: null,
          health_number: null,
          tax_number: null,
          observation: null,
          has_medical_prescription: false,
          total_price: null,
          prescriptions: [
            {
              prescription_code: null,
              code_nr: null,
              access_code: null,
              tube_nr: null,
            }
          ]
        },
        errors: new Errors()
      }
    }
  },
  mounted: function () {
    this.isOpen = true
    if (this.isUpdate) {
      this.prepareForUpdate()
    } else if (this.writableEntities.length === 1) {
      this.selectedEntity = this.writableEntities[0]
    }
  },

  methods: {
    validateTubeNr: function (val, index) {
      //this.isValidTubeNr[index] = val === "" || val == null || val.match(/^[a-zA-Z]{2}[0-9]{5}$/g) !== null;
      this.isValidTubeNr[index] = true
    },
    addPrescription: function () {
      this.newPrescriptions = this.newPrescriptions + 1
      this.form.data.prescriptions.push(
        {
          prescription_code: null,
          code_nr: null,
          access_code: null,
          tube_nr: null,
        }
      )

    },
    handleSelectedTimeInput: function () {
      this.form.errors.clear('selected_time')
      this.selectedTimeSettedByUser = true
    },
    buildSchedule: function (schedulesRequestResponse) {
      // Check if schedule needs to be built
      if (schedulesRequestResponse && schedulesRequestResponse.length > 0 && !schedulesRequestResponse.some(elem => elem.first_starting_hour === null && elem.first_stopping_hour === null && elem.second_starting_hour === null && elem.second_stopping_hour === null)) {
        return
      }
      const WEEKDAY_FIRST_START_DEFAULT = '09:00:00'
      const WEEKDAY_FIRST_STOP_DEFAULT = '13:00:00'
      const WEEKDAY_SECOND_START_DEFAULT = '14:00:00'
      const WEEKDAY_SECOND_STOP_DEFAULT = '18:00:00'

      const WEEKEND_FIRST_START_DEFAULT = '09:00:00'
      const WEEKEND_FIRST_STOP_DEFAULT = '13:00:00'
      const WEEKEND_SECOND_START_DEFAULT = '12:51:00'
      const WEEKEND_SECOND_STOP_DEFAULT = '13:00:00'

      let schedule = []

      const weekdayMappingKeys = {
        'first_starting_hour': WEEKDAY_FIRST_START_DEFAULT,
        'first_stopping_hour': WEEKDAY_FIRST_STOP_DEFAULT,
        'second_starting_hour': WEEKDAY_SECOND_START_DEFAULT,
        'second_stopping_hour': WEEKDAY_SECOND_STOP_DEFAULT
      }

      const weekendMappingKeys = {
        'first_starting_hour': WEEKEND_FIRST_START_DEFAULT,
        'first_stopping_hour': WEEKEND_FIRST_STOP_DEFAULT,
        'second_starting_hour': null,
        'second_stopping_hour': null
      }

      if (schedulesRequestResponse && schedulesRequestResponse.length === 7) {
        schedulesRequestResponse.forEach(elem => {
          if (elem.day_nr > 0 && elem.day_nr <= 5) {
            if (!elem.first_starting_hour) {
              elem.first_starting_hour = weekdayMappingKeys.first_starting_hour
            }
            if (!elem.first_stopping_hour) {
              elem.first_stopping_hour = weekdayMappingKeys.first_stopping_hour
            }
            if (!elem.second_starting_hour) {
              elem.second_starting_hour = weekdayMappingKeys.second_starting_hour
            }
            if (!elem.second_stopping_hour) {
              elem.second_stopping_hour = weekdayMappingKeys.second_stopping_hour
            }
          } else {
            if (!elem.first_starting_hour) {
              elem.first_starting_hour = weekendMappingKeys.first_starting_hour
            }
            if (!elem.first_stopping_hour) {
              elem.first_stopping_hour = weekendMappingKeys.first_stopping_hour
            }
            if (!elem.second_starting_hour) {
              elem.second_starting_hour = weekendMappingKeys.second_starting_hour
            }
            if (!elem.second_stopping_hour) {
              elem.second_stopping_hour = weekendMappingKeys.second_stopping_hour
            }
          }
        })
        schedule = schedulesRequestResponse
      } else {
        for (let i = 1; i <= 5; i++) {
          let obj = {...weekdayMappingKeys}
          obj.day_nr = i
          schedule.push(obj)
        }
        let obj = {...weekdayMappingKeys}
        obj.day_nr = 6
        schedule.push(weekendMappingKeys)
        obj.day_nr = 7
        schedule.push(null)
      }
      this.facilitySchedule = schedule
      if (this.isUpdate && this.form.data.selected_time) {
        this.validateSelectedTime(moment.utc(this.form.data.selected_time).local().format('YYYY-MM-DD HH:mm:ss'))
      }
    },
    validateSelectedTime(datetime) {
      if(this.originalSelectedTime !== null && this.selectedTime === this.originalSelectedTime) {
        this.isValidSchedule = true
        return
      }

      if (datetime === null) {
        this.isValidSchedule = false
        return
      }
      // Segunda 0, Domingo 6
      let dateTimeSchedule = moment(datetime)
      let daySchedule = this.facilitySchedule[dateTimeSchedule.weekday()]
      if (daySchedule === null) {
        this.isValidSchedule = false
        return false
      }

      const momentFirstStart = moment(daySchedule.first_starting_hour, 'hh:mm:ss').set('date', dateTimeSchedule.date()).set('month', dateTimeSchedule.month()).set('year', dateTimeSchedule.year())
      const momentFirstStop = moment(daySchedule.first_stopping_hour, 'hh:mm:ss').set('date', dateTimeSchedule.date()).set('month', dateTimeSchedule.month()).set('year', dateTimeSchedule.year())
      this.isValidSchedule = dateTimeSchedule.isBetween(momentFirstStart, momentFirstStop, null, '[)')
      if (daySchedule.second_starting_hour !== null && daySchedule.second_stopping_hour !== null) {
        const momentSecondStart = moment(daySchedule.second_starting_hour, 'hh:mm:ss').set('date', dateTimeSchedule.date()).set('month', dateTimeSchedule.month()).set('year', dateTimeSchedule.year())
        const momentSecondStop = moment(daySchedule.second_stopping_hour, 'hh:mm:ss').set('date', dateTimeSchedule.date()).set('month', dateTimeSchedule.month()).set('year', dateTimeSchedule.year())

        this.isValidSchedule = dateTimeSchedule.isBetween(momentFirstStart, momentFirstStop, null, '[)') || dateTimeSchedule.isBetween(momentSecondStart, momentSecondStop, null, '[)')
      }
    },
    prepareData: function (data) {
      // handle prescriptions array

      return data
    },
    prepareForUpdate: function () {
      let data = JSON.parse(JSON.stringify(this.appointment))

      this.selectedGender = this.genders.find(gender => gender.value === data.gender)
      this.selectedEntity = this.writableEntities.find(entity => entity.id === data.entity.id) || null
      this.selectedFacility = data.facility ? (this.selectedEntity.facilities.find(facility => facility.id === data.facility.id) || null) : null
      this.selectedTime = this.$options.filters.localTime(data.selected_time, 'YYYY-MM-DD HH:mm')
      this.originalSelectedTime = this.selectedTime
      this.selectedInsurer = data.insurer ? this.insurers.find(insurer => insurer.id === data.insurer.id) : null
      this.selectedPackages = Array.isArray(data.packages) && data.packages.length > 0 ? data.packages.map(hPackage => this.packages.find(p => p.id === hPackage.id)).filter(v => v) : []
      this.phoneNumber = data.phone_number

      Object.keys(data).filter(key => key in this.form.data).forEach(key => {
        this.form.data[key] = data[key]
      })
      if (this.form.data.birthdate) {
        this.form.data.birthdate = moment(this.form.data.birthdate).format('YYYY-MM-DD')
      }
      if(this.appointment.prescriptions.length === 0) {
        this.form.data.prescriptions = [
          {
            prescription_code: null,
            code_nr: null,
            access_code: null,
            tube_nr: null,
          }
        ]
      }

      this.newPrescriptions = this.form.data.prescriptions.length
    },
    submit: function () {
      if (this.isLoading) {
        return
      }

      if (!this.validateForm()) {
        return
      }
      this.isLoading = true

      let data = this.prepareData(this.form.data)

      let request = this.isUpdate ? AppointmentService.update(this.form.data.entity_id, this.appointment.id, data) : AppointmentService.store(this.form.data.entity_id, data)

      request
        .then(appointment => {
          let event = this.isUpdate ? 'appointment-updated' : 'appointment-created'
          let message = this.isUpdate ? this.$t('homelab.messages.appointment_update') : this.$t('homelab.messages.appointment_created')
          this.$emit(event, appointment)
          this.showToast(message, this.$store.state.successToastOptions)
          this.$refs.createOrUpdateAppointmentModal.cancel()
        })
        .catch(errors => {
          let message = null
          if (errors.response.status === 422) {
            message = this.$t('homelab.errors.form_contains_errors')
            this.form.errors.record(errors.response.data.errors, 'homelab.errors.')
          } else if (errors.response.status === 403) {
            message = this.$t('homelab.errors.missing_permissions')
          } else {
            message = this.$t('homelab.errors.something_went_wrong')
          }
          this.showToast(message, this.$store.state.errorToastOptions)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    validateForm: function () {
      let isValid = true

      if (this.form.data.entity_id === null) {
        this.form.errors.set('entity_id', 'homelab.errors.entity_id_is_required')
        isValid = false
      }

      if (this.form.data.facility_id === null) {
        this.form.errors.set('facility_id', 'homelab.errors.facility_id_is_required')
        isValid = false
      }

      if (!isValid) {
        this.showToast(this.$t('homelab.errors.form_contains_errors'), this.$store.state.errorToastOptions)
      }

      return isValid
    },
    onCancel: function () {
      this.isOpen = false
      if (this.isUpdate) {
        this.$emit('cancel')
      } else {
        setTimeout(() => {
          this.$emit('cancel')
        }, 500)
      }
    },
    setLocation: function (location, message) {
      if (location == null) {
        this.map.locations = []
      } else {
        this.form.data.address.lat = location.lat
        this.form.data.address.lng = location.lng
        this.map.locations = [{position: location}]
        this.$refs.googleMap.updateCenter(location)
        this.$refs.googleMap.updateZoom(13)
      }
      this.map.message = message
    },
    prepareForDestroy: function () {
      this.isDestroyingAppointment = true
      this.isOpen = false
    },
    cancelDestroyAppointment: function () {
      this.isDestroyingAppointment = false
      this.isOpen = true
    },
    confirmDestroyAppointment: function () {
      AppointmentService.destroy(this.appointment.entity.id, this.appointment.id)
        .then(response => {
          this.$emit('appointment-deleted')
          this.showToast(this.$t('homelab.messages.appointment_deleted'), this.$store.state.successToastOptions)
        })
        .catch(errors => {
          this.showToast(this.$t('homelab.something_went_wrong'), this.$store.state.errorToastOptions)
        })
        .finally(errors => {
          this.isDestroyingAppointment = false
        })
    }
  },
  watch: {
    'form.data.total_price': {
      handler: function (val) {
        if (val === "" ) {
          this.form.data.total_price = null
        }
      },
      immediate: true
    },
    'form.data.facility_id': {
      handler: debounce(async function (val) {
        if (val) {
          if ((this.isUpdate && this.facilityChanged) || !this.isUpdate) {
            this.selectedTime = null
          }

          let schedules = await FacilityScheduleService.getByFacilitys(this.authServiceToken, {
            facilities: this.form.data.facility_id
          })
          this.buildSchedule(schedules)
        } else {
          this.buildSchedule(null)
        }

        this.facilityChanged = true
      }, 500),
      immediate: true,
      deep: true
    },

    'form.data.address': {
      handler: debounce(function (newValue) {
        try {
          if (newValue.postal_code && newValue.postal_code.length >= 4) {
            this.map.addressConverter.addressToCoords(newValue, false)
              .then(result => {
                if (result !== null) { // request canceled
                  if (Object.keys(result).length > 0 && result.hasOwnProperty('lat') && result.hasOwnProperty('lng')) { // success
                    this.setLocation(result, undefined)
                  } else {
                    this.setLocation(null, this.$t('address.could_not_convert'))
                  }
                }
              })
          } else {
            this.setLocation(null, this.$t('address.insuficient_data_for_conversion'))
          }
        } catch (error) {
          console.log(error)
        }
      }, 500),
      deep: true
    },
    selectedTime: {
      handler: debounce(function (value) {
        if (value !== null) {
          this.form.data.selected_time = this.$options.filters.utcTime(value, 'YYYY-MM-DD HH:mm:ss')
        } else {
          this.selectedTime = null
          this.form.data.selected_time = null
        }

        if (this.selectedTimeSettedByUser) {
          this.validateSelectedTime(value)
        }
      }, 500)
    },
    selectedEntity: function (newValue, oldValue) {
      this.form.data.entity_id = newValue ? newValue.id : null
      if (!newValue || (oldValue && newValue.id !== oldValue.id)) {
        this.selectedFacility = null
      }
    },
    selectedFacility: function (facility) {
      this.form.data.facility_id = facility ? facility.id : null
    },
    selectedInsurer: function (insurer) {
      this.form.data.insurer_id = insurer ? insurer.id : null

      if (this.form.data.insurer_id) {
        this.form.data.packages = []
      }
    },
    selectedPackages: function (packages) {
      this.form.data.packages = packages ? packages.map(p => p.id) : []
    },
    selectedGender: function (value) {
      this.form.data.gender = value ? value.value : null
    },
    phoneNumber: function (value) {
      this.form.data.phone_number = value ? value.replace(/ /g, '') : null
    }
  }
}
</script>

<style scoped>
.form-row-outline {
  border: 2px solid #dcdcdc;
  padding-top: 10px;
  padding-bottom: 20px;
  border-radius: 15px;
}

.validText {
  color: #6cb412;
}

.invalidText {
  color: #ad130b;
}
</style>
